/**
 * One client's booking activity, read out of the month already in hand.
 *
 * Each row is **one call**, carrying its own status history — so a call that
 * was booked and then cancelled reads as one row saying "Cancelled", with
 * "Confirmed → Cancelled" and each change's reason underneath. That is the
 * opposite of what the agenda wants, and deliberately so: the agenda answers
 * "who am I seeing", the panel answers "what happened with this person".
 *
 * The history comes from `fetch-booking-records?include_history=true`, which
 * `useBookings` always asks for. (Before `1f1b855` the endpoint returned every
 * status event as its own row, and this panel showed those raw rows instead.)
 *
 * Scope is the fetched month, and that is a constraint rather than a choice.
 * `fetch-booking-records` refuses somebody else's `user_id` outright (403, see
 * `bookingService`), so there is no per-client history call the frontend may
 * make; filtering the month already in memory is what is available, and it
 * costs no request. Callers must label the month they are showing.
 *
 * No React, no API — unit-testable in isolation.
 */

import { formatLocalDate } from "../../../utils/formatLocalDate";
import { isActiveBooking, normaliseStatus, STATUS } from "./bookingStatus";

/** A Date for an ordering field, or null when it can't be read. */
function toDate(value) {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Epoch millis, or null — for comparisons where a missing value sorts last. */
function msOf(date) {
  return date ? date.getTime() : null;
}

/** Descending by `x`, with nulls last however the comparison runs. */
function descending(a, b) {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

/**
 * One client's calls on one coach's calendar, newest first.
 *
 * The `employeeId` filter matters on the caller's own month, which is fetched
 * unfiltered on purpose and therefore also contains the bookings on their
 * *other* employee profile (see `useBookings`). On a manager reading one coach
 * the fetch is already narrowed, so it is a no-op — correct in both shapes.
 *
 * Ordered by start time descending, then `created_at` descending: the most
 * recent call first, which is also the API's own order.
 *
 * @param {Array<Object>} records - The month's bookings (`useBookings().bookings`).
 * @param {{ clientId?: string, employeeId?: string }} scope
 * @returns {Array<Object>} Rows carrying `startsAt`, `endsAt` (when readable) and
 *   `history` (oldest first; see `statusHistoryOf`).
 */
export function clientTrailForEmployee(records, { clientId, employeeId } = {}) {
  if (!clientId) return [];

  return (Array.isArray(records) ? records : [])
    .filter((record) => {
      if (String(record?.user_id) !== String(clientId)) return false;
      // Unset means "don't narrow" rather than "match nothing" — the same
      // convention the service's own id params use.
      if (!employeeId) return true;
      return String(record?.coach_id) === String(employeeId);
    })
    .map((record) => ({
      ...record,
      startsAt: toDate(record?.start_time),
      endsAt: toDate(record?.end_time),
      history: statusHistoryOf(record),
    }))
    .sort(
      (a, b) =>
        descending(msOf(a.startsAt), msOf(b.startsAt)) ||
        descending(msOf(toDate(a.created_at)), msOf(toDate(b.created_at)))
    );
}

/**
 * The local calendar day a trail row falls on, as `YYYY-MM-DD` — the same key
 * `bucketBookingsByLocalDate` files the agenda under — or null with no start.
 *
 * @param {Object} record - A row from `clientTrailForEmployee`.
 * @returns {string|null}
 */
export function dayKeyOf(record) {
  const startsAt = toDate(record?.startsAt ?? record?.start_time);
  return startsAt ? formatLocalDate(startsAt) : null;
}

/**
 * One day's calls with the live ones ahead of the cancelled ones, each part
 * keeping the trail's order (newest start first). A call that still holds its
 * time is what the day is about; a later slot that was called off is history.
 * Array.prototype.sort is stable, so this only moves cancellations down.
 */
function liveFirst(rows) {
  return [...rows].sort((a, b) => Number(!isActiveBooking(a)) - Number(!isActiveBooking(b)));
}

/**
 * The trail's calls on one local day, live calls first and newest start first
 * within each part — so the top row is the day's last live call, the one the
 * agenda row stands for (`latestPerUser`).
 *
 * @param {Array<Object>} trail - From `clientTrailForEmployee`.
 * @param {string} dayKey - `YYYY-MM-DD`.
 * @returns {Array<Object>}
 */
export function trailOnDay(trail, dayKey) {
  if (!dayKey) return [];
  return liveFirst(
    (Array.isArray(trail) ? trail : []).filter((record) => dayKeyOf(record) === dayKey)
  );
}

/**
 * The trail grouped by local day: `firstDayKey`'s group first (the day the
 * drawer was opened on), then the other days in the trail's own order — newest
 * first — and rows with no readable start last, under a null key. Within a
 * day, live calls come before cancelled ones, as in `trailOnDay`.
 *
 * @param {Array<Object>} trail - From `clientTrailForEmployee`.
 * @param {string} [firstDayKey]
 * @returns {Array<{ dayKey: string|null, date: Date|null, rows: Array<Object> }>}
 */
export function trailByDay(trail, firstDayKey) {
  const groups = new Map();
  (Array.isArray(trail) ? trail : []).forEach((record) => {
    const dayKey = dayKeyOf(record);
    if (!groups.has(dayKey)) {
      groups.set(dayKey, { dayKey, date: toDate(record?.startsAt ?? record?.start_time), rows: [] });
    }
    groups.get(dayKey).rows.push(record);
  });
  const list = [...groups.values()];
  const rank = (group) => (group.dayKey === null ? 2 : group.dayKey === firstDayKey ? 0 : 1);
  // Array.prototype.sort is stable, so each rank keeps the trail's order.
  return list
    .sort((a, b) => rank(a) - rank(b))
    .map((group) => ({ ...group, rows: liveFirst(group.rows) }));
}

/**
 * A booking's status changes, oldest first, each with a parsed `changedAt`.
 *
 * The API already sends them oldest first (`created_at ASC, id ASC`); this
 * re-sorts anyway so a row assembled some other way still reads in order, and
 * drops entries with no status title since they have nothing to say.
 *
 * @param {Object} record - A booking row, optionally carrying `status_history`.
 * @returns {Array<{ id: string, status: string, reason: string, changedAt: Date|null }>}
 */
export function statusHistoryOf(record) {
  const raw = Array.isArray(record?.status_history) ? record.status_history : [];
  return raw
    .filter((entry) => entry?.booking_status)
    .map((entry) => ({
      id: entry.id,
      status: entry.booking_status,
      reason: typeof entry.status_reason === "string" ? entry.status_reason.trim() : "",
      changedAt: toDate(entry.created_at),
    }))
    .sort((a, b) => (msOf(a.changedAt) ?? 0) - (msOf(b.changedAt) ?? 0));
}

/**
 * The panel's header line: how many calls, and how many still to come.
 *
 * Rows are one per call, so this counts them directly. Cancelled calls are
 * left out of both numbers: `isActiveBooking` is the existing rule for "still
 * holds the time", and counting a cancellation as a call would make the number
 * climb every time one is called off.
 *
 * @param {Array<Object>} trail - From `clientTrailForEmployee`.
 * @param {Date} [now] - Injectable for tests.
 * @returns {{ calls: number, upcoming: number }}
 */
export function summariseClientActivity(trail, now = new Date()) {
  const live = (Array.isArray(trail) ? trail : []).filter(isActiveBooking);
  const cutoff = now.getTime();

  return {
    calls: live.length,
    upcoming: live.filter((record) => {
      const startsAt = record.startsAt || toDate(record?.start_time);
      return Boolean(startsAt) && startsAt.getTime() > cutoff;
    }).length,
  };
}

/**
 * The client's rating of a call, 1–5, or null when there is none to show.
 *
 * Written from the app once the call has ended (B14 in the backend analysis);
 * FlexBoard only reads it. Stored as a float and validated 1–5 on write, but a
 * value outside that range — or a non-number — reads as "not rated" here
 * rather than as a broken bar.
 *
 * @param {{ rating?: number|string|null }} record
 * @returns {number|null}
 */
export function ratingOf(record) {
  if (record?.rating == null || record.rating === "") return null;
  const rating = Number(record.rating);
  return Number.isFinite(rating) && rating >= 1 && rating <= 5 ? rating : null;
}

/**
 * The client's written feedback on a call, trimmed — "" when there is none.
 *
 * @param {{ feedback?: string|null }} record
 * @returns {string}
 */
export function feedbackOf(record) {
  return typeof record?.feedback === "string" ? record.feedback.trim() : "";
}

const RATING_LABELS = ["Poor", "Fair", "Good", "Very good", "Excellent"];

/**
 * A word for a rating, to the nearest star: 4 → "Very good".
 *
 * @param {number|null} rating - From `ratingOf`.
 * @returns {string} "" for no rating.
 */
export function ratingLabel(rating) {
  if (rating == null) return "";
  return RATING_LABELS[Math.min(5, Math.max(1, Math.round(rating))) - 1];
}

/**
 * Whether a call has client feedback to show: it is **currently Completed**,
 * and the client left a rating, written feedback, or both. The activity card
 * shows its rating chip and feedback section only then.
 *
 * The status is the latest one, not any in the history. Feedback is given on a
 * completed call, but the call can be corrected afterwards (Completed → No
 * Show), and the stored rating outlives that — it can't be cleared (COALESCE).
 * Once the call no longer reads as completed, the rating it got as one is
 * hidden; set it back to Completed and it shows again.
 *
 * @param {Object} record
 * @returns {boolean}
 */
export function hasClientFeedback(record) {
  if (normaliseStatus(record?.booking_status) !== STATUS.COMPLETED) return false;
  return ratingOf(record) != null || feedbackOf(record) !== "";
}
